{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "$id": "execution_record_schema.json",
  "title": "ExecutionRecord",
  "description": "Canonical schema for execution log records - the universal event stream",
  "additionalProperties": false,
  "type": "object",
  "required": [
    "id",
    "taskId",
    "type",
    "title",
    "result"
  ],
  "properties": {
    "id": {
      "type": "string",
      "pattern": "^\\d{10}-exec-[a-z0-9-]{1,50}$",
      "maxLength": 66,
      "description": "Unique identifier for the execution log entry (10 timestamp + 1 dash + 4 'exec' + 1 dash + max 50 slug = 66 max)",
      "examples": [
        "1752275000-exec-refactor-queries",
        "1752361200-exec-api-externa-caida"
      ]
    },
    "taskId": {
      "type": "string",
      "pattern": "^\\d{10}-task-[a-z0-9-]{1,50}$",
      "maxLength": 66,
      "description": "ID of the parent task this execution belongs to (10 timestamp + 1 dash + 4 'task' + 1 dash + max 50 slug = 66 max)"
    },
    "type": {
      "type": "string",
      "enum": [
        "analysis",
        "progress",
        "blocker",
        "completion",
        "info",
        "correction"
      ],
      "description": "Semantic classification of the execution event",
      "examples": [
        "progress",
        "analysis",
        "blocker",
        "completion"
      ]
    },
    "title": {
      "type": "string",
      "minLength": 1,
      "maxLength": 256,
      "description": "Human-readable title for the execution (used to generate ID)",
      "examples": [
        "Refactor de queries N+1",
        "API Externa Caída",
        "Plan de implementación OAuth2"
      ]
    },
    "result": {
      "type": "string",
      "minLength": 10,
      "maxLength": 22000,
      "description": "The tangible, verifiable output or result of the execution. \nThis is the \"WHAT\" - evidence of work or event summary.\n"
    },
    "notes": {
      "type": "string",
      "maxLength": 6500,
      "description": "Optional narrative, context and decisions behind the execution.\nThis is the \"HOW\" and \"WHY\" - the story behind the result.\n"
    },
    "references": {
      "type": "array",
      "items": {
        "type": "string",
        "maxLength": 500
      },
      "default": [],
      "description": "Optional list of typed references to relevant commits, files, PRs, or external documents.\nShould use typed prefixes for clarity and trazabilidad (see execution_protocol_appendix.md):\n- commit:     Git commit SHA\n- pr:         Pull Request number\n- file:       File path (relative to repo root)\n- url:        External URL\n- issue:      GitHub Issue number\n- task:       TaskRecord ID\n- exec:       ExecutionRecord ID (for corrections or dependencies)\n- changelog:  ChangelogRecord ID\n"
    }
  },
  "examples": [
    {
      "id": "1752275500-exec-refactor-queries",
      "taskId": "1752274500-task-optimizar-api",
      "type": "progress",
      "title": "Refactor de queries N+1",
      "result": "Refactorizados 3 queries N+1 a un solo JOIN optimizado. Performance mejoró de 2.5s a 200ms.",
      "notes": "Identificados 3 N+1 queries en el endpoint /api/search. Aplicado eager loading y caching de relaciones.",
      "references": [
        "commit:b2c3d4e",
        "file:src/api/search.ts"
      ]
    },
    {
      "id": "1752361200-exec-api-externa-caida",
      "taskId": "1752274500-task-optimizar-api",
      "type": "blocker",
      "title": "API Externa Caída",
      "result": "No se puede continuar con testing de integración. API de pagos devuelve 503.",
      "notes": "La API de pagos de terceros (api.payments.com) está devolviendo errores 503. Contactado soporte del proveedor. ETA de resolución: 2-3 horas.",
      "references": [
        "url:https://status.payments.com"
      ]
    },
    {
      "id": "1752188000-exec-plan-oauth-implementation",
      "taskId": "1752274500-task-oauth-implementation",
      "type": "analysis",
      "title": "Plan de implementación OAuth2",
      "result": "Documento de diseño técnico completado. 5 sub-tareas identificadas con estimaciones de complejidad.",
      "notes": "Evaluadas 3 opciones: NextAuth.js (elegida), Passport.js, custom implementation. NextAuth.js por madurez y soporte de múltiples providers.",
      "references": [
        "file:docs/oauth-design.md"
      ]
    },
    {
      "id": "1752707800-exec-oauth-completed",
      "taskId": "1752274500-task-oauth-implementation",
      "type": "completion",
      "title": "OAuth Implementation Completed",
      "result": "Sistema OAuth2 completamente implementado, testeado y deployado a staging. 95% test coverage. Todos los acceptance criteria cumplidos.",
      "notes": "Implementación finalizada. Code review aprobado. Tests E2E passing. Ready para changelog y deploy a producción.",
      "references": [
        "pr:456",
        "commit:def789abc",
        "url:https://staging.app.com/login"
      ]
    },
    {
      "id": "1752275600-exec-cambio-estrategia-redis",
      "taskId": "1752274500-task-oauth-implementation",
      "type": "info",
      "title": "Cambio de estrategia: Usar Redis para sessions",
      "result": "Decisión: Migrar de JWT stateless a sessions en Redis por requisito de revocación inmediata.",
      "notes": "Durante code review se identificó requisito crítico: revocar sesiones inmediatamente (ej: compromiso de cuenta). JWT stateless no permite esto sin lista negra compleja. Redis sessions permite revocación instantánea.",
      "references": [
        "issue:567",
        "url:https://redis.io/docs/manual/keyspace-notifications/"
      ]
    },
    {
      "id": "1752275700-exec-correccion-metricas",
      "taskId": "1752274500-task-optimizar-api",
      "type": "correction",
      "title": "Corrección: Métricas de performance",
      "result": "Corrección de execution 1752275500-exec-refactor-queries: El performance fue 200ms, no 50ms como se reportó.",
      "notes": "Error de tipeo en execution original. La mejora real fue de 2.5s a 200ms (no 50ms). Sigue siendo significativa (92% mejora) pero números correctos son importantes para métricas.",
      "references": [
        "exec:1752275500-exec-refactor-queries"
      ]
    }
  ]
}